import { Injectable } from '@angular/core';
import { 
    LTFilterRule, 
    LTFilterGroup, 
    LTFilter, 
    LTFilterOperator, 
    LTFilterOperatorDef, 
    LTFilterDataType, 
    LTFilterLogic,
    LTFilterColumn, 
    LT_FILTER_OPERATORS,
} from '../models/lt-filter.models';

@Injectable({ providedIn: 'root' })
export class LTFilterService {

    //#region Create Filter Utils
    public createFilterRule(
        field: string,
        operator: LTFilterOperator,
        value: unknown,
        ignoreCase = false,
    ): LTFilterRule {
        return { field, operator, value, ignoreCase };
    }

    public createFilterGroup(logic: LTFilterLogic, filters: (LTFilterRule | LTFilterGroup)[]): LTFilterGroup {
        return { logic, filters };
    }
    //#endregion Create Filter Utils

    //#region Quick Filter Utils
    /**
     * Quick filter method that creates a filter rule and applies it to data in one call
     * @param data Array of data to filter
     * @param field Field name to filter on (supports nested paths like 'user.name')
     * @param value Value to filter by
     * @param operator Filter operator (defaults to 'eq')
     * @param ignoreCase Whether to ignore case for string comparisons (defaults to false)
     * @returns Filtered array
     * 
     * @example
     * // Simple equality filter
     * const results = filterService.filterBy(users, 'status', 'active');
     * 
     * // String contains filter with case insensitive
     * const results = filterService.filterBy(users, 'name', 'john', 'contains', true);
     * 
     * // Numeric comparison
     * const results = filterService.filterBy(users, 'age', 25, 'gt');
     * 
     * // Nested field
     * const results = filterService.filterBy(users, 'profile.department', 'Engineering');
     */
    public filterBy<T>(
        data: T[],
        field: string,
        value: unknown,
        operator: LTFilterOperator = 'eq',
        ignoreCase = false,
    ): T[] {
        if (!data || data.length === 0) return data;

        const filterRule = this.createFilterRule(field, operator, value, ignoreCase);
        return this.applyFilter(data, filterRule);
    }

    /**
     * Quick filter method that applies multiple filter rules with AND logic
     * @param data Array of data to filter
     * @param filters Array of filter configurations
     * @returns Filtered array
     * 
     * @example
     * const results = filterService.filterByMultiple(users, [
     *   { field: 'status', value: 'active', operator: 'eq' },
     *   { field: 'name', value: 'john', operator: 'contains', ignoreCase: true },
     *   { field: 'age', value: 21, operator: 'gte' }
     * ]);
     */
    public filterByMultiple<T>(
        data: T[],
        filters: Array<{
            field: string;
            value: unknown;
            operator?: LTFilterOperator;
            ignoreCase?: boolean;
        }>,
    ): T[] {
        if (!data || data.length === 0 || !filters || filters.length === 0) return data;
        
        const filterRules = filters.map(f => 
            this.createFilterRule(f.field, f.operator || 'eq', f.value, f.ignoreCase || false),
        );
        
        const filterGroup = this.createFilterGroup('and', filterRules);
        return this.applyFilter(data, filterGroup);
    }

    /**
     * Quick search across multiple fields using contains operator
     * @param data Array of data to search
     * @param searchTerm Search term to look for
     * @param fields Array of field names to search in
     * @param ignoreCase Whether to ignore case (defaults to true)
     * @returns Filtered array containing items that match the search term in any of the specified fields
     * 
     * @example
     * // Search for 'john' in name, email, and department fields
     * const results = filterService.searchAcrossFields(users, 'john', ['name', 'email', 'profile.department']);
     */
    public searchAcrossFields<T>(
        data: T[],
        searchTerm: string,
        fields: string[],
        ignoreCase = true,
    ): T[] {
        if (!data || data.length === 0 || !searchTerm || !fields || fields.length === 0) return data;
        
        const filterRules = fields.map(field => 
            this.createFilterRule(field, 'contains', searchTerm, ignoreCase),
        );
        
        const filterGroup = this.createFilterGroup('or', filterRules);
        return this.applyFilter(data, filterGroup);
    }

    //#endregion

    //#region Helper Utils
    public getOperatorsForType(type: LTFilterDataType): LTFilterOperatorDef[] {
        return LT_FILTER_OPERATORS.filter(op => op.supportedTypes.includes(type));
    }

    public getFilterType(fields: LTFilterColumn[], field: string): LTFilterDataType {
        return fields.find(f => f.field === field)?.type;
    }

    public isFilterGroup(filter: LTFilter): filter is LTFilterGroup {
        return 'logic' in filter && 'filters' in filter;
    }

    public isFilterRule(filter: LTFilter): filter is LTFilterRule {
        return 'field' in filter && 'operator' in filter;
    }

    public serializeFilter(filter: LTFilter): string {
        return JSON.stringify(filter);
    }

    public deserializeFilter(serialized: string): LTFilter | null {
        try {
            return JSON.parse(serialized) as LTFilter;
        } catch {
            return null;
        }
    }
    
    private getNestedValue(obj: unknown, path: string): unknown {
        return path.split('.').reduce((current, key) => {
            return current && typeof current === 'object' && key in current
                ? (current as Record<string, unknown>)[key]
                : undefined;
        }, obj);
    }

    private compareValues(a: unknown, b: unknown): number {
        // Handle dates
        if (a instanceof Date && b instanceof Date) {
            return a.getTime() - b.getTime();
        }
        
        // Handle numbers
        if (typeof a === 'number' && typeof b === 'number') {
            return a - b;
        }
        
        // Handle strings
        if (typeof a === 'string' && typeof b === 'string') {
            return a.localeCompare(b);
        }
        
        // Fallback comparison
        return String(a).localeCompare(String(b));
    }

    private compareForEquality(itemValue: unknown, filterValue: unknown, ignoreCase: boolean): boolean {
        if (ignoreCase && typeof itemValue === 'string' && typeof filterValue === 'string') {
        return itemValue.toLowerCase() === filterValue.toLowerCase();
        }
        return itemValue === filterValue;
    }
    //#region Helper Utils

    //#region Filter Evaluation
    public applyFilter<T>(data: T[], filter: LTFilter): T[] {
        if (!filter) return data;

        return data.filter(item => this.evaluateFilter(item, filter));
    }

    private evaluateFilter(item: unknown, filter: LTFilter): boolean {
        if (this.isFilterGroup(filter)) {
            return this.evaluateFilterGroup(item, filter);
        }

        if (this.isFilterRule(filter)) {
            return this.evaluateFilterRule(item, filter);
        }

        return true;
    }

    private evaluateFilterGroup(item: unknown, filter: LTFilterGroup): boolean {
        const results = filter.filters.map(f => this.evaluateFilter(item, f));

        return filter.logic === 'and'
            ? results.every(r => r)
            : results.some(r => r);
    }

    private evaluateFilterRule(item: unknown, filter: LTFilterRule): boolean {
        const itemValue = this.getNestedValue(item, filter.field);
        const filterValue = filter.value;
        const { operator, ignoreCase = false } = filter;

        // Handle null/undefined checks first
        if (operator === 'isnull') return itemValue == null || itemValue == undefined;
        if (operator === 'isnotnull') return itemValue != null || itemValue != undefined;
        if (operator === 'isempty') return itemValue === '';
        if (operator === 'isnotempty') return itemValue !== '';

        // If item value is null/undefined and we're not checking for null/empty, return false
        if (itemValue == null) return false;

        // Convert values for comparison if needed
        const normalizedItemValue = ignoreCase && typeof itemValue === 'string' 
            ? itemValue.toLowerCase() 
            : itemValue;
        const normalizedFilterValue = ignoreCase && typeof filterValue === 'string'
            ? filterValue.toLowerCase()
            : filterValue;
        
        switch(operator) {
            case 'eq':
                return normalizedItemValue === normalizedFilterValue;
            case 'neq':
                return normalizedItemValue !== normalizedFilterValue;
            case 'contains':
                return typeof normalizedItemValue === 'string' && 
                    typeof normalizedFilterValue === 'string' &&
                    normalizedItemValue.includes(normalizedFilterValue);
            case 'doesnotcontain':
                return typeof normalizedItemValue === 'string' && 
                    typeof normalizedFilterValue === 'string' &&
                    !normalizedItemValue.includes(normalizedFilterValue);
            case 'startswith':
                return typeof normalizedItemValue === 'string' && 
                    typeof normalizedFilterValue === 'string' &&
                    normalizedItemValue.startsWith(normalizedFilterValue);
            case 'endswith':
                return typeof normalizedItemValue === 'string' && 
                    typeof normalizedFilterValue === 'string' &&
                    normalizedItemValue.endsWith(normalizedFilterValue);
            case 'gt':
                return this.compareValues(itemValue, filterValue) > 0;
            case 'gte':
                return this.compareValues(itemValue, filterValue) >= 0;
            case 'lt':
                return this.compareValues(itemValue, filterValue) < 0;
            case 'lte':
                return this.compareValues(itemValue, filterValue) <= 0;
            case 'in':
                return Array.isArray(filterValue) && 
                    filterValue.some(val => this.compareForEquality(normalizedItemValue, val, ignoreCase));
            case 'notin':
                return Array.isArray(filterValue) && 
                    !filterValue.some(val => this.compareForEquality(normalizedItemValue, val, ignoreCase));
            default:
                return true;
            
        }
    }
    //#endregion Filter Evaluation
    

    

    
}

